"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { Api_Paths } from "@/lib/api/paths";
import { Auth_Event_Success } from "@/lib/auth/client/events";
import { Auth_Access_Get, Auth_Fetch, Auth_Session_Restore } from "@/lib/auth/client/session";
import {
  Theme_Document_Apply,
  Theme_Parse,
  Theme_Storage_Read,
  Theme_Storage_Write,
} from "@/lib/theme/client";
import { Theme_Mode_Light, type Theme_Mode } from "@/lib/theme/types";

type Theme_Context_Value = {
  Theme: Theme_Mode;
  Theme_Ready: boolean;
  Theme_Set: (Mode: Theme_Mode) => Promise<void>;
};

const Theme_Context = createContext<Theme_Context_Value | null>(null);

type Theme_Provider_Props = {
  children: ReactNode;
  Theme_Initial?: Theme_Mode;
};

export function Theme_Provider({ children, Theme_Initial }: Theme_Provider_Props) {
  const [Theme, Theme_State_Set] = useState<Theme_Mode>(Theme_Initial ?? Theme_Mode_Light);
  const [Theme_Ready, Theme_Ready_Set] = useState(false);

  useEffect(() => {
    const Stored = Theme_Storage_Read();
    const Boot = Stored ?? Theme_Initial ?? Theme_Mode_Light;
    Theme_State_Set(Boot);
    Theme_Document_Apply(Boot);
    Theme_Ready_Set(true);

    let Cancelled = false;

    async function Theme_Account_Hydrate() {
      try {
        if (!Auth_Access_Get()) {
          await Auth_Session_Restore();
        }
        if (!Auth_Access_Get()) {
          return;
        }
        const Response = await Auth_Fetch(Api_Paths.V1.User_Me);
        if (!Response.ok || Cancelled) {
          return;
        }
        const Body = (await Response.json()) as { user?: { theme?: unknown } };
        const Account_Theme = Theme_Parse(Body.user?.theme);
        if (!Account_Theme || Cancelled) {
          return;
        }
        Theme_State_Set(Account_Theme);
        Theme_Storage_Write(Account_Theme);
      } catch {
        /* guest or offline — keep local */
      }
    }

    void Theme_Account_Hydrate();

    function Theme_Auth_Success_Handle() {
      void Theme_Account_Hydrate();
    }
    window.addEventListener(Auth_Event_Success, Theme_Auth_Success_Handle);

    return () => {
      Cancelled = true;
      window.removeEventListener(Auth_Event_Success, Theme_Auth_Success_Handle);
    };
  }, [Theme_Initial]);

  const Theme_Set = useCallback(async (Mode: Theme_Mode) => {
    Theme_State_Set(Mode);
    Theme_Storage_Write(Mode);

    try {
      if (!Auth_Access_Get()) {
        await Auth_Session_Restore();
      }
      if (!Auth_Access_Get()) {
        return;
      }
      await Auth_Fetch(Api_Paths.V1.User_Me, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ theme: Mode }),
      });
    } catch {
      /* local theme still applied */
    }
  }, []);

  const Value = useMemo(
    () => ({ Theme, Theme_Ready, Theme_Set }),
    [Theme, Theme_Ready, Theme_Set],
  );

  return <Theme_Context.Provider value={Value}>{children}</Theme_Context.Provider>;
}

export function Theme_Use(): Theme_Context_Value {
  const Value = useContext(Theme_Context);
  if (!Value) {
    throw new Error("Theme_Use requires Theme_Provider");
  }
  return Value;
}
